import outerSelected from '@/assets/images/frame/outer-selected.svg';
import { DrawPlayerImage } from '@/utils/drawing';
import clsx from 'clsx';
import { KonvaEventObject } from 'konva/lib/Node';
import { Stage as RStage } from 'konva/lib/Stage';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { Group, Layer, Stage } from 'react-konva';
import ColorIcon from '../Icons/ColorIcon';
import { UndoIcon } from '../Icons/CommonIcons';

interface CanvasProps {
  drawing: LineProps[];
  setDrawing?: (drawing: LineProps[]) => void;
  isViewerMode?: boolean;

  submissionEnabled?: boolean;
  onSubmit?: () => void;
}

export interface LineProps {
  points: number[];
  stroke: string;
  width: number;
  tool: string;
  canvasWidth: number;
  canvasHeight: number;
}

/** Maximum width for the canvas */
const MAX_CANVAS_WIDTH = 517;

/** Aspect ratio for consistent scaling */
export const ASPECT_RATIO = 7 / 10;

/** Available color selections for drawing */
const COLORS = [
  { color: '#f58c29', alt: 'orange color selection' },
  { color: '#db0632', alt: 'red color selection' },
  { color: '#d51668', alt: 'fuschia color selection' },
  { color: '#99d9d9', alt: 'sky blue color selection' },
  { color: '#20145f', alt: 'navy blue color selection' },
  { color: '#18864a', alt: 'hunter green color selection' }
];

/**
 * Responsive Canvas component for drawing
 * @param {CanvasProps} props - Component properties
 * @param {React.Ref} ref - Forwarded ref for the Konva Stage
 */
const Canvas = forwardRef<RStage, CanvasProps>(
  ({ setDrawing, drawing, isViewerMode, onSubmit, submissionEnabled }, ref) => {
    const [tool] = useState<'pen' | 'eraser'>('pen');
    const [color, setColor] = useState<string>(COLORS[0].color);
    const [width] = useState<number>(7);

    /**
     * Calculate responsive canvas size based on container
     * @returns {Object} Calculated canvas dimensions
     */
    const calculateCanvasSize = (): { width: number; height: number } => {
      // Use viewport width with some padding
      const availableWidth = Math.min(window.innerWidth * 0.9, MAX_CANVAS_WIDTH);
      const availableHeight = window.innerHeight * 0.6;

      let newCanvasWidth = availableWidth;
      let newCanvasHeight = availableWidth / ASPECT_RATIO;

      // Adjust if height exceeds available space
      if (newCanvasHeight > availableHeight) {
        newCanvasHeight = availableHeight;
        newCanvasWidth = availableHeight * ASPECT_RATIO;
      }

      return {
        width: Math.floor(newCanvasWidth),
        height: Math.floor(newCanvasHeight)
      };
    };

    // State for canvas size with initial responsive calculation
    const [canvasSize, setCanvasSize] = useState<{ width: number; height: number }>(
      calculateCanvasSize()
    );

    // Ref to track drawing state
    const isDrawing = useRef(false);
    const layerRef = useRef<HTMLDivElement>(null);

    /**
     * Handle responsive resizing
     */
    useEffect(() => {
      const handleResize = () => {
        setCanvasSize(calculateCanvasSize());
      };

      // Add resize listener
      window.addEventListener('resize', handleResize);

      // Initial calculation
      handleResize();

      // Cleanup listener
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    /**
     * Handle mouse down event for drawing
     * @param {KonvaEventObject} e - Mouse or touch event
     */
    const handleMouseDown = (e: KonvaEventObject<MouseEvent> | KonvaEventObject<TouchEvent>) => {
      if (isViewerMode || !setDrawing) return;

      const stage = e.target.getStage();
      if (!stage) return;

      const pos = stage.getPointerPosition();
      if (!pos) return;

      isDrawing.current = true;
      setDrawing([
        ...drawing,
        {
          tool,
          points: [pos.x, pos.y],
          stroke: color,
          width: (canvasSize.width / MAX_CANVAS_WIDTH) * width,
          canvasWidth: canvasSize.width,
          canvasHeight: canvasSize.height
        }
      ]);
    };

    /**
     * Handle mouse move event for continuous drawing
     * @param {KonvaEventObject} e - Mouse or touch event
     */
    const handleMouseMove = (e: KonvaEventObject<MouseEvent> | KonvaEventObject<TouchEvent>) => {
      if (isViewerMode || !setDrawing || !isDrawing.current) return;

      const stage = e.target.getStage();
      if (!stage) return;

      const pos = stage.getPointerPosition();
      if (
        !pos ||
        pos.x < width ||
        pos.y < width ||
        pos.x > canvasSize.width - width ||
        pos.y > canvasSize.height - width
      ) {
        if (e.type == 'touchmove') isDrawing.current = false;
        return;
      }

      const lastLine = drawing[drawing.length - 1];
      const newPoints = lastLine.points.concat([pos.x, pos.y]);

      drawing.splice(drawing.length - 1, 1, { ...lastLine, points: newPoints });
      setDrawing(drawing.concat());
    };

    /**
     * Handle mouse up event to stop drawing
     */
    const handleMouseUp = () => {
      isDrawing.current = false;
    };

    /**
     * Undo last drawing action
     */
    const handleUndo = () => {
      if (setDrawing) {
        setDrawing(drawing.slice(0, -1));
      }
    };

    /**
     * Submit drawing
     */
    const handleSubmit = () => {
      onSubmit?.();
    };

    const cardFrameOuterSelected = new Image();
    cardFrameOuterSelected.src = outerSelected;

    return (
      <div className="flex flex-col w-full h-full mx-auto max-w-full overflow-hidden">
        <div
          ref={layerRef}
          className={clsx(
            'flex items-center justify-center',
            'relative touch-none w-full',
            'mx-auto p-8'
          )}
        >
          <Stage
            ref={ref}
            width={canvasSize.width}
            height={canvasSize.height}
            onMouseDown={handleMouseDown}
            onTouchStart={handleMouseDown}
            onMousemove={handleMouseMove}
            onTouchMove={handleMouseMove}
            onMouseup={handleMouseUp}
            onTouchEnd={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <Layer>
              <Group x={canvasSize.width / 2} y={canvasSize.height / 2}>
                <DrawPlayerImage
                  drawing={drawing}
                  cardSize={{
                    width: canvasSize.width,
                    height: canvasSize.height
                  }}
                />
              </Group>
            </Layer>
          </Stage>
        </div>
        {!isViewerMode && (
          <>
            <div className="flex flex-row flex-wrap justify-center items-center space-x-8 p-8">
              {COLORS.map((colorSelector) => (
                <button
                  aria-label={colorSelector.alt}
                  key={colorSelector.color}
                  onClick={() => setColor(colorSelector.color)}
                  className={clsx(
                    'cursor-pointer rounded-full p-2',
                    color === colorSelector.color ? 'ring-2 ring-[#20145f]' : ''
                  )}
                >
                  <ColorIcon color={colorSelector.color} />
                </button>
              ))}
            </div>
            <div className="flex flex-row justify-center items-center space-x-16 p-8">
              <button className="p-8" onClick={handleUndo}>
                <UndoIcon />
              </button>
              <button
                onClick={handleSubmit}
                disabled={!drawing.length && submissionEnabled}
                className={clsx(
                  'rounded-full font-bold py-12 px-64 bg-[#D51668] disabled:opacity-50',
                  'text-white text-m text-center',
                  'transition duration-300 ease-in-out'
                )}
              >
                Submit
              </button>
            </div>
          </>
        )}
      </div>
    );
  }
);

Canvas.displayName = 'Canvas';

export default Canvas;
